import React, { useEffect, useState, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faPlus, faSearch, faTimes, faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";

import { BookCard } from "../components/BookCard";
import { Routess } from "../Routes/routes";
import { Model } from "../components/Model";
import { useAuth } from "../store/AuthContext";
import { sendError, sendSuccess } from "../helpers/Helper";
import { Textbox } from "../components/Textbox";
import { SelectBox } from "../components/SelectBox";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import axios from "axios";
import { useApi } from "../helpers/useApi";
import { API_URL_ENDPOINT } from "../appsetting";
import SignatureUploader from "../components/SignatureUploader";
import NoCover from "../assets/img/nocover.png";
import { ConfirmDeleteModal } from "../components/ConfirmDeleteModal";

export const Books = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const callApi = useApi();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all"); // all, published, draft
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [coverPreview, setCoverPreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  console.log("Books Render:", { loading, statusFilter }); // Debugging

  const observer = useRef();
  const lastBookElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Main Form
  const bookForm = useForm({
    defaultValues: {
      id: "0",
      title: "",
      description: "",
      bookAttributes: [],
    },
  });

  const { register, handleSubmit, reset, formState: { errors, isSubmitting }, watch, control } = bookForm;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bookAttributes",
  });

  // Fetch Categories & Attributes
  const fetchCategories = useCallback(async () => {
    try {
      const response = await callApi(API_URL_ENDPOINT.BooksCategory + "?pageNo=1&pageSize=100");
      if (response?.status) {
        setCategories(response.data.items || []);
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  }, []); // Removed callApi dependency to match BookDetail.jsx and prevent loop

  const fetchAttributes = useCallback(async () => {
    try {
      const response = await callApi(`${API_URL_ENDPOINT.Books}/attributes`);
      console.log('Attributes Response:', response); // Debugging
      if (response.status) {
        setAttributes(response.data ?? []);
      }
    } catch {
      console.error("Failed to load attributes");
    }
  }, []); // Removed callApi dependency

  useEffect(() => {
    fetchCategories();
    fetchAttributes();
  }, [fetchCategories, fetchAttributes]);

  const fetchBooks = useCallback(async (pageNum, search = "", status = "all") => {
    setLoading(true);
    try {
      const filters = [];

      // Status Filters
      if (status === "all") {
        filters.push("ShowPublished:true");
        filters.push("ShowDraft:true");
      } else if (status === "published") {
        filters.push("ShowPublished:true");
        filters.push("ShowDraft:false");
      } else if (status === "draft") {
        filters.push("ShowPublished:false");
        filters.push("ShowDraft:true"); // Ensure drafts are visible even with IsPublished:false
      }

      if (search) {
        filters.push(`SearchText:${search}`);
      }

      const queryParams = [
        `filters=${filters.join(";")}`,
        `pageNo=${pageNum}`,
        `pageSize=12`,
        `sortOrder=desc`
      ].join("&");

      const response = await callApi(`${API_URL_ENDPOINT.Books}?${queryParams}`, {
        method: 'GET'
      });

      if (response?.status) {
        const newBooks = response.data.items || []; // Note: data.items based on standard pagination response
        if (pageNum === 1) {
          setBooks(newBooks);
        } else {
          setBooks(prev => [...prev, ...newBooks]);
        }
        setHasMore(newBooks.length === 12);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    fetchBooks(page, searchTerm, statusFilter);
  }, [page, searchTerm, statusFilter, fetchBooks]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (status) => {
    if (status === statusFilter) return;
    setBooks([]);
    setLoading(true);
    setStatusFilter(status);
    setPage(1);
  };

  const handleAddBook = async (data) => {
    try {
      const payload = {
        id: "0",
        title: data.title,
        categoryID: data.categoryId,
        coverImage: coverPreview?.fileKey ?? "",
        description: data.description,
        attributes: data.bookAttributes?.map((a) => ({
          attributeID: parseInt(a.attributeId, 10),
          attributeValue: a.attributeValue,
        })) || [],
      };

      const response = await callApi(API_URL_ENDPOINT.ADD_BOOK, {
        method: 'POST',
        data: payload
      });

      if (response?.status) {
        sendSuccess("Book added successfully");
        setShowModal(false);
        reset();
        setCoverPreview(null);
        setPage(1);
        fetchBooks(1, searchTerm, statusFilter);
      } else {
        sendError(response?.message || "Failed to add book");
      }
    } catch (e) {
      console.error(e);
      sendError(e?.message || "An error occurred");
    }
  };

  const handleView = (id) => {
    navigate(Routess.Books.path + "/" + id);
  };

  const handleDeleteBook = (book) => {
    setBookToDelete(book);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!bookToDelete) return;
    setIsDeleting(true);
    try {
      const res = await callApi(API_URL_ENDPOINT.Books + "/" + bookToDelete.id, {
        method: 'DELETE',
      });
      if (res?.status) {
        sendSuccess("Book deleted successfully");
        setBooks(prev => prev.filter(b => b.id !== bookToDelete.id));
        setShowDeleteModal(false);
        setBookToDelete(null);
      }
    } catch (e) {
      sendError(e.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveCoverToDB = async (data) => setCoverPreview(data);

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-lightGray">
        <div>
          <h4 className="text-xl font-semibold text-dark m-0">Library Books</h4>
          <div className="inline-flex items-center gap-1 bg-slate-100 p-1.5 rounded-xl mt-3">
            {[
              { id: "all", label: "All Books" },
              { id: "published", label: "Published" },
              { id: "draft", label: "Drafts" }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleStatusChange(tab.id)}
                className={`
                  relative px-5 py-2 text-sm font-medium rounded-lg transition-all duration-300 ease-out
                  ${statusFilter === tab.id
                    ? "bg-white text-slate-800 shadow-sm ring-1 ring-black/5"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"}
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative group w-full md:w-64">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray group-focus-within:text-primary transition-colors">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              placeholder="Search books..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-lightGray bg-light focus:bg-white focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button
            onClick={() => {
              reset({ id: "0", title: "", description: "", bookAttributes: [] });
              setCoverPreview(null);
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-primary to-orange-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg shadow-primary/30 hover:-translate-y-0.5 transition-all flex items-center gap-2 whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Book
          </button>
        </div>
      </div>

      {/* Books Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {books.map((book, index) => {
          if (books.length === index + 1) {
            return (
              <BookCard
                ref={lastBookElementRef}
                key={book.id || index}
                book={book}
                onBookClick={() => handleView(book.id)}
                onDelete={handleDeleteBook}
              />
            );
          } else {
            return (
              <BookCard
                key={book.id || index}
                book={book}
                onBookClick={() => handleView(book.id)}
                onDelete={handleDeleteBook}
              />
            );
          }
        })}
      </div>

      {/* Empty State */}
      {books.length === 0 && !loading && (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <p className="text-gray text-lg">No books found. Try adding one!</p>
        </div>
      )}

      {/* Loading Spinner */}
      {loading && (
        <div className="flex justify-center mt-8 mb-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
        </div>
      )}

      {/* Add Book Modal */}
      <Model show={showModal} size="xl">
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center mr-3 shadow-lg shadow-primary/30">
              <FontAwesomeIcon icon={faPlus} className="text-white text-sm" />
            </div>
            <h5 className="font-semibold text-base m-0">Add New Book</h5>
          </div>
          <button
            onClick={() => setShowModal(false)}
            className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all text-white"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="bg-light">
          <FormProvider {...bookForm}>
            <form onSubmit={handleSubmit(handleAddBook)}>
              <div className="flex flex-col lg:flex-row">
                {/* Left Column: Cover Image */}
                <div className="lg:w-1/4 p-6 bg-white border-r border-lightGray text-center">
                  <h6 className="text-sm font-semibold text-dark mb-4 text-left">📖 Book Cover</h6>
                  <div className="mx-auto mb-4 w-[180px] h-[270px] rounded-xl overflow-hidden shadow-md border border-lightGray relative bg-gray-50">
                    <SignatureUploader
                      imageURL={coverPreview?.documentURL || NoCover}
                      onUploaded={saveCoverToDB}
                      height={270}
                      width={180}
                      showEdit
                    />
                  </div>
                  <p className="text-xs text-gray mb-4">Click to upload cover image.</p>

                  <div className="flex flex-col gap-2 mt-auto">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-orange-600 text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Book'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="w-full py-2 rounded-lg bg-white border border-lightGray text-gray font-semibold text-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Right Column: Details */}
                <div className="lg:w-3/4 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Textbox
                      Label="Book Title"
                      name="title"
                      register={register}
                      validationRules={{ required: "Title is required" }}
                      errors={errors}
                      watch={watch}
                      Placeholder="e.g. History of Jambhani"
                    />
                    <SelectBox
                      Label="Category"
                      name="categoryId"
                      register={register}
                      errors={errors}
                      watch={watch}
                      validationRules={{ required: "Category is required" }}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.title}
                        </option>
                      ))}
                    </SelectBox>
                  </div>

                  <div className="mb-4">
                    <Textbox
                      Label="Description"
                      name="description"
                      register={register}
                      errors={errors}
                      watch={watch}
                      as="textarea"
                      rows={4}
                      Placeholder="Enter book description..."
                      style={{ height: 'auto' }}
                    />
                  </div>

                  {/* Dynamic Attributes */}
                  <div className="mt-6 pt-4 border-t border-lightGray">
                    <div className="flex justify-between items-center mb-4">
                      <h6 className="text-sm font-semibold text-dark m-0">Dynamic Attributes</h6>
                      <button
                        type="button"
                        onClick={() => append({ attributeId: "", attributeValue: "" })}
                        className="px-3 py-1.5 rounded-lg bg-light text-primary border border-primary/20 text-xs font-semibold hover:bg-primary hover:text-white transition-colors flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Attribute
                      </button>
                    </div>

                    <div className="space-y-3">
                      {fields.map((field, index) => (
                        <div key={field.id} className="flex gap-3 items-start p-3 bg-white border border-lightGray rounded-xl shadow-sm">
                          <div className="flex-1">
                            <SelectBox
                              name={`bookAttributes.${index}.attributeId`}
                              register={register}
                              errors={errors}
                              watch={watch}
                              wrapperClassName="mb-0"
                              validationRules={{ required: true }}
                            >
                              <option value="">Select Attribute</option>
                              {attributes.map(attr => (
                                <option key={attr.id} value={attr.id}>{attr.name}</option>
                              ))}
                            </SelectBox>
                          </div>
                          <div className="flex-1">
                            <Textbox
                              name={`bookAttributes.${index}.attributeValue`}
                              register={register}
                              errors={errors}
                              watch={watch}
                              Placeholder="Value"
                              validationRules={{ required: true }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="mt-1 p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                      {fields.length === 0 && (
                        <p className="text-center text-gray text-xs py-4 border border-dashed border-gray-300 rounded-xl">
                          No attributes added yet. Click "Add Attribute" to add details like Author, Publisher, etc.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </Model>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemName={bookToDelete?.title}
        requireConfirmation={true}
        isLoading={isDeleting}
      />
    </div>
  );
}
